import React from "react";
import PropTypes from "prop-types";
import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift,
  arrow,
  hide,
  useDismiss,
  useInteractions,
  FloatingPortal,
} from "@floating-ui/react";
import AppUtils from "@src/utils/appUtils";

const GAP = 12; // px between the target and the box, matches the triangle's reach

// direction (this atom's public API) -> Floating UI placement. "start"/"end"
// map to left/right for top/bottom placements in this LTR app.
const DIRECTION_TO_PLACEMENT = {
  up: "top",
  down: "bottom",
  left: "left",
  right: "right",
  "up-left": "top-end",
  "up-right": "top-start",
  "down-left": "bottom-end",
  "down-right": "bottom-start",
};

// Keyed by the resolved side (placement's first segment) so the triangle
// always points back at the target, even after `flip` swaps the side to
// avoid a viewport edge.
const TRIANGLE_SHAPE = {
  top: { width: 28, height: 14, points: "0,0 28,0 14,14" }, // box above target: points down
  bottom: { width: 28, height: 14, points: "0,14 28,14 14,0" }, // box below target: points up
  left: { width: 14, height: 28, points: "0,0 0,28 14,14" }, // box left of target: points right
  right: { width: 14, height: 28, points: "14,0 14,28 0,14" }, // box right of target: points left
};

const OPPOSITE_SIDE = {
  top: "bottom",
  bottom: "top",
  left: "right",
  right: "left",
};

const DialogBubble = ({
  target,
  children,
  direction = "down-right",
  visible = true,
  onDismiss,
  bgColor = "primary-100",
  borderColor = "neutral-200",
  display = "inline-block",
  className = "",
  dismissOnOutsideClick = false,
  overlayColor,
  revealDelayMs = 2000,
}) => {
  const arrowRef = React.useRef(null);

  // Lets consumers hold off rendering the bubble (and overlay) for a beat
  // after `visible` flips true — e.g. so the user sees the underlying page
  // before it's revealed. Hiding is always immediate.
  const [shown, setShown] = React.useState(revealDelayMs > 0 ? false : visible);
  React.useEffect(() => {
    if (!visible) {
      setShown(false);
      return;
    }
    if (revealDelayMs <= 0) {
      setShown(true);
      return;
    }
    const revealTimeout = setTimeout(() => setShown(true), revealDelayMs);
    return () => clearTimeout(revealTimeout);
  }, [visible, revealDelayMs]);

  const { refs, floatingStyles, placement, middlewareData, context } =
    useFloating({
      placement: DIRECTION_TO_PLACEMENT[direction] ?? "bottom-start",
      open: shown,
      onOpenChange: (open) => !open && onDismiss?.(),
      whileElementsMounted: autoUpdate,
      middleware: [
        offset(GAP),
        flip(),
        shift({ padding: 8 }),
        arrow({ element: arrowRef }),
        hide({ strategy: "referenceHidden" }),
      ],
    });

  const dismiss = useDismiss(context, { enabled: dismissOnOutsideClick });
  const { getFloatingProps } = useInteractions([dismiss]);

  // Close the bubble if its target scrolls out of view within a clipping
  // ancestor (e.g. a carousel or scrollable list) instead of leaving it
  // floating next to nothing.
  const referenceHidden = !!middlewareData.hide?.referenceHidden;
  React.useEffect(() => {
    if (shown && referenceHidden) {
      onDismiss?.();
    }
  }, [shown, referenceHidden, onDismiss]);

  const side = placement.split("-")[0];
  const { width, height, points } = TRIANGLE_SHAPE[side];
  const arrowData = middlewareData.arrow;
  const arrowDepth = side === "top" || side === "bottom" ? height : width;

  const arrowStyle = {
    left: arrowData?.x != null ? `${arrowData.x}px` : "",
    top: arrowData?.y != null ? `${arrowData.y}px` : "",
    [OPPOSITE_SIDE[side]]: `${-(arrowDepth - 1)}px`,
  };

  const spotlight = !!overlayColor && shown;

  return (
    <>
      <div
        ref={refs.setReference}
        className={`${display} ${spotlight ? "relative z-80" : ""}`}
      >
        {target}
      </div>
      {shown && (
        <FloatingPortal id="modal-root">
          {spotlight && (
            <div
              className={`fixed inset-0 z-70 backdrop-blur-sm bg-${overlayColor}-100/70 animate-fadeIn`}
            />
          )}
          <div
            ref={refs.setFloating}
            style={floatingStyles}
            {...getFloatingProps()}
            className={`z-999 w-72 max-w-[calc(100vw-2rem)] border rounded-xl shadow-lg p-4 bg-${bgColor} border-${borderColor} ${onDismiss ? "pr-8" : ""} ${spotlight ? "animate-fadeIn" : ""} ${className}`}
          >
            <svg
              ref={arrowRef}
              className="absolute overflow-visible"
              style={arrowStyle}
              width={width}
              height={height}
              viewBox={`0 0 ${width} ${height}`}
            >
              <polygon points={points} className={`fill-${bgColor}`} />
            </svg>
            {!!onDismiss && (
              <button
                onClick={onDismiss}
                className="absolute top-2 right-2 text-neutral-400 hover:text-neutral-600 text-xl leading-none"
                aria-label="Dismiss"
              >
                ×
              </button>
            )}
            {children}
          </div>
        </FloatingPortal>
      )}
    </>
  );
};

export const dialogBubbleControls = {
  target: {
    propType: PropTypes.node.isRequired,
    controlType: "text",
    description: "The element the dialog bubble should appear next to",
    disable: true,
  },
  children: {
    propType: PropTypes.node,
    controlType: "text",
    description: "Dialog bubble content",
    disable: true,
  },
  direction: {
    propType: PropTypes.string,
    controlType: "select",
    options: [
      "up",
      "down",
      "left",
      "right",
      "up-left",
      "up-right",
      "down-left",
      "down-right",
    ],
    description:
      "Position of the dialog bubble relative to the target, and the direction the triangle points. Auto-flips to stay in the viewport.",
    defaultValue: "down-right",
  },
  visible: {
    propType: PropTypes.bool,
    controlType: "boolean",
    description: "Whether the dialog bubble is shown",
    defaultValue: true,
  },
  onDismiss: {
    propType: PropTypes.func,
    description:
      "Called when the dismiss (×) button is clicked. Omit to hide the button.",
    disable: true,
  },
  bgColor: {
    propType: PropTypes.string,
    controlType: "select",
    options: [
      "primary-100",
      "neutral-100",
      "orange-100",
      "pink-100",
      "teal-100",
    ],
    description: "Background colour token, also used for the triangle fill",
    defaultValue: "primary-100",
  },
  borderColor: {
    propType: PropTypes.string,
    description: "Border colour token",
    defaultValue: "neutral-200",
  },
  display: {
    propType: PropTypes.string,
    controlType: "select",
    options: ["inline-block", "block"],
    description: "Display mode of the wrapper around the target",
    defaultValue: "inline-block",
  },
  dismissOnOutsideClick: {
    propType: PropTypes.bool,
    controlType: "boolean",
    description:
      "Also call onDismiss when the user clicks/taps anywhere outside the bubble",
    defaultValue: false,
  },
  overlayColor: {
    propType: PropTypes.string,
    controlType: "select",
    options: [
      undefined,
      "purple",
      "blue",
      "orange",
      "pink",
      "teal",
      "green",
      "lightblue",
      "yellow",
    ],
    description:
      "Colour token for a full-screen dimming overlay that spotlights the target and bubble above everything else. Omit for no overlay.",
  },
  revealDelayMs: {
    propType: PropTypes.number,
    controlType: "number",
    description:
      "Delay, in ms, between `visible` becoming true and the bubble actually rendering — lets the user see the underlying page first. Hiding is always immediate. Pass 0 to show immediately.",
    defaultValue: 2000,
  },
};

DialogBubble.propTypes = AppUtils.getPropTypeFromControls(dialogBubbleControls);

export default DialogBubble;
